import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Award, CheckCircle, ArrowRight, Globe, GraduationCap, FileText, 
  Users, Target, Heart, Lightbulb, ChevronDown, ChevronUp, HelpCircle,
  Mail, BookOpen, Shield, TrendingUp, DollarSign, AlertCircle
} from 'lucide-react';

const Scholarship = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const purposes = [
    'Expand access to humanitarian and development education',
    'Support professionals from developing and resource-constrained contexts',
    'Strengthen local and regional leadership capacity',
    'Help learners build practical, policy, research, and management skills',
    'Support professionals working across NGOs, public institutions, civil society, and social impact organizations',
    'Promote cross-regional learning across the Afro-Asia corridor',
    'Reduce tuition barriers for qualified applicants',
    'Encourage locally grounded leadership with globally relevant knowledge'
  ];

  const programs = [
    {
      title: 'Master of Humanitarian Action & Development',
      duration: '1 Year',
      fullFee: 2000,
      scholarshipFee: 200,
      link: '/program/masters'
    },
    {
      title: 'Master of Development Project & Program Management',
      duration: '9 Months',
      fullFee: 3500,
      scholarshipFee: 350,
      link: '/program/masters-ppm'
    },
    {
      title: 'Doctor of Humanitarian Action & Development',
      duration: '3 Years',
      fullFee: 10800,
      scholarshipFee: 1080,
      link: '/program/doctoral'
    }
  ];

  const eligibleApplicants = [
    'Humanitarian professionals',
    'Development practitioners',
    'NGO staff',
    'Public sector professionals',
    'Civil society professionals',
    'Social impact professionals',
    'Project and program staff',
    'MEAL, grants, finance, and operations staff',
    'Researchers and policy practitioners',
    'Early-career and mid-career professionals',
    'Career changers seeking to enter the humanitarian or development sector',
    'Emerging leaders from Africa and Asia',
    'Professionals serving communities in developing or crisis-affected contexts'
  ];

  const eligibilityCriteria = [
    'Country or region of residence or citizenship',
    'Professional or academic interest in humanitarian or development work',
    'Commitment to public service, social impact, NGO work, research, or community development',
    'Academic readiness for the selected program',
    'Ability to study online',
    'English language proficiency',
    'Completion of the application process',
    'Submission of required documents',
    'Availability of scholarship places for the relevant program or intake'
  ];

  const reviewSteps = [
    { step: 1, title: 'Application Submission', description: 'Applicants complete the online application form and provide the required information.' },
    { step: 2, title: 'Eligibility Review', description: 'The Admissions Team reviews the applicant\'s academic background, professional profile, motivation, country or regional context, and program suitability.' },
    { step: 3, title: 'Scholarship Assessment', description: 'Eligible applicants are considered for the GIHDS Global Scholarship Initiative based on the scholarship criteria and available places.' },
    { step: 4, title: 'Admission and Scholarship Decision', description: 'Successful applicants may receive an admissions offer confirming the scholarship-adjusted tuition.' },
    { step: 5, title: 'Tuition Payment and Enrollment', description: 'Applicants complete the required tuition payment to confirm enrollment and receive access to the program.' }
  ];

  const selectionConsiderations = [
    'Relevance of the applicant\'s background to the selected program',
    'Professional or academic motivation',
    'Commitment to humanitarian, development, NGO, public sector, or social impact work',
    'Potential contribution to communities, organizations, or professional practice',
    'Ability to complete the program successfully',
    'Financial accessibility considerations',
    'Regional representation',
    'Availability of scholarship-supported places'
  ];

  const conditions = [
    'The scholarship is applied only to tuition fees.',
    'The scholarship is not paid directly to the applicant.',
    'The scholarship does not cover internet, devices, software, personal expenses, travel, printing, courier services, or other external costs.',
    'Scholarship approval is subject to admission review.',
    'Scholarship places may be limited by program, intake, or institutional capacity.',
    'The Institute may request additional information or documentation during review.',
    'The scholarship may be withdrawn if an applicant provides false, misleading, or incomplete information.',
    'Scholarship recipients must comply with academic, administrative, and payment requirements.',
    'The scholarship does not guarantee graduation, employment, promotion, visa approval, professional licensing, or external recognition.'
  ];

  const learningAreas = [
    'Humanitarian action', 'Sustainable development', 'Development project management',
    'Development program management', 'MEAL', 'Project finance',
    'Donor systems and proposal development', 'Humanitarian leadership',
    'International humanitarian law', 'Protection, gender, and inclusion',
    'Emergency response', 'Research methods', 'Policy analysis',
    'Global health', 'Applied research and dissertation work',
    'AI productivity in humanitarian and development settings'
  ];

  const whatMakesDifferent = [
    {
      icon: Target,
      title: 'Access-Oriented',
      description: 'The initiative is designed to reduce tuition barriers for learners who may otherwise be unable to access professional humanitarian and development education.'
    },
    {
      icon: BookOpen,
      title: 'Professionally Relevant',
      description: 'Scholarship-supported programs focus on knowledge and skills connected to real humanitarian and development practice.'
    },
    {
      icon: Globe,
      title: 'Flexible and Online',
      description: 'Programs are delivered online, allowing learners to study from different locations while balancing work and professional responsibilities.'
    },
    {
      icon: Users,
      title: 'Afro-Asia Focus',
      description: 'The initiative gives particular attention to learners from Africa and Asia, supporting capacity development across regions where humanitarian and development needs are significant.'
    },
    {
      icon: Shield,
      title: 'Institutional Support',
      description: 'Scholarships are issued by GIHDS as part of its institutional commitment to access, professional learning, and global development education.'
    }
  ];

  const faqs = [
    { question: 'What is the GIHDS Global Scholarship Initiative?', answer: 'The GIHDS Global Scholarship Initiative is an institutional tuition scholarship program designed to reduce tuition barriers for eligible applicants, especially learners from Africa and Asia and other resource-constrained contexts.' },
    { question: 'Is the scholarship a cash award?', answer: 'No. The scholarship is not a cash payment, stipend, grant, or living allowance. It is a tuition reduction applied directly to the program fee.' },
    { question: 'Who can apply?', answer: 'Applicants from Africa, Asia, and other developing or resource-constrained contexts may be considered, especially those with a demonstrated interest in humanitarian action, development work, NGO practice, public service, research, leadership, or social impact.' },
    { question: 'Is the scholarship automatic?', answer: 'No. Scholarship approval is subject to admission review, eligibility assessment, and availability of scholarship-supported places.' },
    { question: 'How much tuition support is available?', answer: 'Eligible applicants may receive up to a 90% tuition scholarship, depending on the program and admissions review.' },
    { question: 'Which programs are currently supported?', answer: 'The initiative currently supports selected GIHDS programs including the Master of Humanitarian Action & Development, Master of Development Project & Program Management, and Doctor of Humanitarian Action & Development.' },
    { question: 'Does the scholarship cover all costs?', answer: 'No. The scholarship applies only to tuition fees. It does not cover internet, devices, software, printed documents, courier services, travel, or other personal costs.' },
    { question: 'Can working professionals apply?', answer: 'Yes. The initiative is designed to support working professionals who need flexible online learning options.' },
    { question: 'Can recent graduates apply?', answer: 'Yes. Recent graduates may apply where they meet the entry requirements for the selected program.' },
    { question: 'Does receiving a scholarship guarantee graduation?', answer: 'No. Scholarship recipients must successfully complete the academic and administrative requirements of their program.' },
    { question: 'Does the scholarship guarantee employment?', answer: 'No. The scholarship supports access to education. It does not guarantee employment, promotion, migration, licensing, or professional recognition.' },
    { question: 'How do I apply?', answer: 'Applicants should complete the relevant program application form. Scholarship consideration is part of the admissions and scholarship review process.' }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="scholarship-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#163A59] border-t-4 border-[#C82032]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-10 h-10 text-white/90" />
              <p className="text-sm font-medium uppercase tracking-widest text-white/80">
                Institutional Scholarship Program
              </p>
            </div>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              GIHDS Global Scholarship Initiative
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8">
              Expanding access to humanitarian and development education for qualified learners 
              from Africa, Asia, and resource-constrained contexts.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/admissions"
                className="inline-flex items-center gap-2 bg-white text-[#C82032] hover:bg-slate-100 px-8 py-4 rounded-sm font-medium transition-all"
                data-testid="hero-apply-btn"
              >
                Apply for Scholarship
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/programs"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-sm font-medium transition-all"
              >
                View Eligible Programs
              </Link>
              <a
                href="mailto:admissions@gihds.org"
                className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/30 px-8 py-4 rounded-sm font-medium transition-all"
              >
                <Mail size={18} />
                Contact Admissions
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                About the Initiative
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Expanding Access to Humanitarian and Development Education
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">
                  The <strong>GIHDS Global Scholarship Initiative</strong> is an institutional tuition 
                  scholarship program established by the Geneva Institute of Humanitarian & Development 
                  Studies to make professional humanitarian and development education more accessible 
                  to qualified learners from Africa, Asia, and other resource-constrained contexts.
                </p>
                <p className="mb-6">
                  The initiative reflects a simple belief: access to high-quality, professionally relevant 
                  education should not be limited by geography, income level, or economic circumstance.
                </p>
                <p>
                  Through this initiative, eligible applicants may receive substantial tuition support 
                  toward selected GIHDS programs, helping them pursue advanced learning in humanitarian 
                  action, development practice, project and program management, leadership, research, 
                  policy, and applied professional skills.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why the Initiative Exists */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                The Challenge
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Why the Scholarship Initiative Exists
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">
                  Humanitarian and development challenges are increasingly complex. Crises are becoming 
                  more interconnected, development needs are expanding, and organizations require 
                  professionals who can think critically, lead responsibly, manage programs effectively, 
                  and respond to communities with competence and integrity.
                </p>
                <p className="mb-6">
                  Yet many capable professionals and emerging leaders across Africa and Asia face 
                  financial barriers that limit their access to professional education.
                </p>
                <p>
                  The <strong>GIHDS Global Scholarship Initiative</strong> was created to reduce this barrier. 
                  The initiative supports learners who demonstrate commitment to humanitarian service, 
                  development work, public service, NGO practice, social impact, research, leadership, 
                  or community-centered change.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-[#163A59] p-8 md:p-12 text-white"
            >
              <Heart className="w-12 h-12 opacity-80 mb-6" />
              <h3 className="font-['Source_Serif_4'] text-2xl font-bold mb-6">Our Purpose</h3>
              <p className="text-white/80 mb-6">The purpose of the GIHDS Global Scholarship Initiative is to:</p>
              <ul className="space-y-3">
                {purposes.slice(0, 6).map((purpose) => (
                  <li key={purpose} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C82032] mt-0.5 flex-shrink-0" />
                    <span className="text-white/90 text-sm">{purpose}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Afro-Asia Learning Corridor */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-[#163A59] p-8 md:p-12 text-white">
                <Globe className="w-12 h-12 text-[#C82032] mb-6" />
                <h3 className="font-['Source_Serif_4'] text-2xl font-bold mb-4">
                  The Afro-Asia Learning Corridor
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  A growing community of professionals, practitioners, and emerging leaders committed 
                  to humanitarian action, development practice, public service, NGO management, 
                  research, and social impact.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Regional Focus
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                A Scholarship for the Afro-Asia Learning Corridor
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">
                  The GIHDS Global Scholarship Initiative gives particular attention to learners from 
                  <strong> Africa and Asia</strong>, where many humanitarian and development professionals 
                  work in complex environments but often face limited access to affordable postgraduate 
                  and professional education.
                </p>
                <p>
                  GIHDS recognizes that many of the world's humanitarian and development challenges are 
                  experienced most directly in these regions. The Institute also recognizes that many of 
                  the most committed professionals working in these contexts need access to flexible, 
                  affordable, and professionally relevant education.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What the Scholarship Covers */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center text-white mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest opacity-80 mb-4">
              Scholarship Value
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              What the Scholarship Covers
            </h2>
            <p className="text-lg opacity-90 max-w-3xl mx-auto">
              The GIHDS Global Scholarship Initiative provides a <strong>tuition reduction</strong> for 
              eligible applicants. It is applied directly to the program tuition fee. It is not a cash 
              payment, living allowance, travel grant, stipend, or external financial aid award.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-sm p-8 text-center text-white">
              <p className="text-6xl font-bold mb-2">90%</p>
              <p className="text-sm opacity-80">Maximum Tuition Reduction</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-8 text-center text-white">
              <p className="text-6xl font-bold mb-2">3</p>
              <p className="text-sm opacity-80">Programs Supported</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-8 text-center text-white">
              <p className="text-6xl font-bold mb-2">$14K+</p>
              <p className="text-sm opacity-80">Total Potential Savings</p>
            </div>
          </div>

          <div className="bg-white/5 border border-white/20 p-6 text-white/90 text-center">
            <p className="text-sm">
              <strong>Important:</strong> The scholarship does not reduce the seriousness of the program. 
              Scholarship recipients are expected to participate responsibly, complete academic requirements, 
              and uphold the standards of the Institute.
            </p>
          </div>
        </div>
      </section>

      {/* Scholarship-Supported Programs */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Eligible Programs
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              Current Scholarship-Supported Programs
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 overflow-hidden hover:border-[#163A59]/30 hover:shadow-lg transition-all"
              >
                <div className="bg-[#163A59] p-6 text-white">
                  <GraduationCap className="w-8 h-8 mb-3 opacity-80" />
                  <h3 className="font-semibold text-lg">{program.title}</h3>
                  <p className="text-sm opacity-70">{program.duration}</p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#F1F5F8] p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Full Fee</p>
                      <p className="text-lg font-bold text-slate-400 line-through">USD {program.fullFee.toLocaleString()}</p>
                    </div>
                    <div className="bg-[#C82032]/10 p-4 text-center">
                      <p className="text-xs text-[#C82032] uppercase tracking-wide mb-1">With Scholarship</p>
                      <p className="text-lg font-bold text-[#C82032]">USD {program.scholarshipFee.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 flex items-center justify-center gap-2 text-green-700 mb-6">
                    <Award size={18} />
                    <span className="font-medium">Save USD {(program.fullFee - program.scholarshipFee).toLocaleString()}</span>
                  </div>
                  <Link
                    to={program.link}
                    className="block text-center text-[#163A59] font-medium hover:underline"
                  >
                    View Program Details →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tuition Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white border border-slate-200 overflow-hidden max-w-4xl mx-auto"
          >
            <div className="bg-[#163A59] p-4 text-white text-center">
              <h3 className="font-semibold">Tuition Comparison Table</h3>
            </div>
            <div className="overflow-x-auto">
              <p className="text-xs text-slate-500 px-4 pt-3 sm:hidden">← Swipe to see all columns →</p>
              <table className="w-full min-w-[560px]">
                <thead className="bg-[#F1F5F8]">
                  <tr>
                    <th className="text-left px-6 py-4 font-medium text-slate-900">Program</th>
                    <th className="text-right px-6 py-4 font-medium text-slate-900">Full Program Fee</th>
                    <th className="text-right px-6 py-4 font-medium text-slate-900">Scholarship Tuition</th>
                    <th className="text-right px-6 py-4 font-medium text-slate-900">Tuition Saving</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((program, index) => (
                    <tr key={program.title} className={index !== programs.length - 1 ? 'border-b border-slate-200' : ''}>
                      <td className="px-6 py-4 text-slate-600">{program.title}</td>
                      <td className="px-6 py-4 text-right text-slate-900 font-semibold">USD {program.fullFee.toLocaleString()}</td>
                      <td className="px-6 py-4 text-right text-[#C82032] font-bold">USD {program.scholarshipFee.toLocaleString()}</td>
                      <td className="px-6 py-4 text-right text-green-600 font-semibold">USD {(program.fullFee - program.scholarshipFee).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who the Scholarship Is For */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Target Audience
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Who the Scholarship Is For
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                The GIHDS Global Scholarship Initiative is intended for applicants who demonstrate 
                academic readiness, professional interest, and commitment to humanitarian or development work.
              </p>
              <p className="text-slate-600 font-medium">Eligible applicants may include:</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-slate-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {eligibleApplicants.map((applicant) => (
                  <div key={applicant} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#163A59] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">{applicant}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Requirements
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              General Eligibility Criteria
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Applicants may be considered for the scholarship based on the following criteria. 
              Scholarship approval is not automatic. Each application is reviewed by the Institute.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {eligibilityCriteria.map((criteria, index) => (
                <motion.div
                  key={criteria}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4 bg-white p-4 border border-slate-200"
                >
                  <div className="w-8 h-8 bg-[#163A59]/10 text-[#163A59] flex items-center justify-center rounded-sm font-semibold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-slate-600 pt-1">{criteria}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Scholarship Review Process */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center text-white mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#FCA5A5] mb-4">
              How It Works
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Scholarship Review Process
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              The scholarship review is integrated into the admissions process.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {reviewSteps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#C82032] text-white flex items-center justify-center rounded-sm font-bold text-lg">
                      {step.step}
                    </div>
                    {index < reviewSteps.length - 1 && (
                      <div className="w-0.5 h-12 bg-slate-700 mx-auto mt-2"></div>
                    )}
                  </div>
                  <div className="pt-2">
                    <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-slate-400">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Selection Considerations */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Assessment Criteria
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Selection Considerations
              </h2>
              <p className="text-slate-600 leading-relaxed">
                GIHDS may consider the following during scholarship review:
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-3"
            >
              {selectionConsiderations.map((consideration) => (
                <div key={consideration} className="flex items-start gap-3 bg-[#F1F5F8] p-4">
                  <CheckCircle className="w-5 h-5 text-[#163A59] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">{consideration}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scholarship Conditions */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Terms & Conditions
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              Scholarship Conditions
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              The GIHDS Global Scholarship Initiative is subject to the following conditions:
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white border border-slate-200 p-8">
            <div className="space-y-4">
              {conditions.map((condition, index) => (
                <div key={condition} className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#C82032] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">{condition}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why This Initiative Matters */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Our Impact
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Why This Initiative Matters
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">
                  The humanitarian and development sectors need capable professionals who can lead programs, 
                  manage resources, analyze complex problems, support affected communities, design effective 
                  interventions, and make responsible decisions.
                </p>
                <p className="mb-6">
                  Many professionals across Africa and Asia already carry this responsibility in demanding 
                  environments. What they often lack is affordable access to structured, flexible, and 
                  professionally relevant education.
                </p>
                <p className="mb-6">
                  <strong>The GIHDS Global Scholarship Initiative seeks to close part of that gap.</strong>
                </p>
                <p className="text-[#C82032] font-semibold text-xl">
                  It is not charity. It is an investment in human capacity.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whatMakesDifferent.slice(0, 4).map((item, index) => (
                  <div key={item.title} className="bg-[#F1F5F8] p-6">
                    <item.icon className="w-8 h-8 text-[#163A59] mb-3" />
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Common Questions
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.03 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-[#F1F5F8] transition-colors"
                  data-testid={`faq-toggle-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-[#163A59] flex-shrink-0" />
                    <span className="font-medium text-slate-900">{faq.question}</span>
                  </div>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                
                <AnimatePresence>
                  {expandedFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 pt-0 border-t border-slate-200">
                        <p className="text-slate-600 pl-8">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-[#C82032] to-[#A81828]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Award className="w-16 h-16 text-white/90 mx-auto mb-8" />
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Apply for the GIHDS Global<br />Scholarship Initiative
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Advance your knowledge. Strengthen your professional capacity. Join a global learning 
              community committed to humanitarian action, development practice, leadership, and social impact.
            </p>
            <p className="text-white/80 mb-10">
              Eligible applicants from Africa and Asia may receive substantial tuition support through the 
              <strong className="text-white"> GIHDS Global Scholarship Initiative</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#C82032] hover:bg-slate-100 px-10 py-4 rounded-sm font-medium transition-all"
                data-testid="final-apply-btn"
              >
                Apply for Scholarship
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/programs"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-10 py-4 rounded-sm font-medium transition-all"
              >
                View Eligible Programs
              </Link>
              <a
                href="mailto:admissions@gihds.org"
                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/30 px-10 py-4 rounded-sm font-medium transition-all"
              >
                <Mail size={18} />
                Contact Admissions
              </a>
            </div>

            <div className="text-white/70 text-sm max-w-2xl mx-auto">
              <p className="mb-4">
                <strong className="text-white">Professional Disclaimer:</strong> The GIHDS Global Scholarship 
                Initiative is an institutional tuition scholarship provided by GIHDS. It is not a cash award, 
                external grant, or government scholarship. Scholarship approval is subject to admission review, 
                eligibility assessment, and availability of scholarship-supported places.
              </p>
              <p>
                <strong className="text-white">Contact:</strong>{' '}
                <a href="mailto:admissions@gihds.org" className="underline hover:text-white">
                  admissions@gihds.org
                </a>
                {' | '}
                <a href="https://www.gihds.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
                  www.gihds.org
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Scholarship;
